import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useHealthStatusChangeTable } from '@/components/pages/SettingsPage/MainContent/NotificationPreferences/useHealthStatusChangeTable';
import { BaseTable } from '@/components';
import { SectionEmptyState } from '@/components/pages/AssetsPreviewPage/AssetsOverview/EmptyStates';
import { NotificationPreferencesTableRef } from '@/components/pages/SettingsPage/MainContent/NotificationPreferences/NotificationPreferences';
import { groupAssetsBySite, updateNotificationPreferencesCache } from './utils';
import { isEqual } from 'lodash';
import { NOTIFICATION_PREFERENCES_TABLE_CLASS_NAMES } from '@/components/pages/SettingsPage/MainContent/NotificationPreferences/const';
import { useNestedParameterUpdate } from '@/components/pages/SettingsPage/MainContent/NotificationPreferences/useNestedParameterUpdate';
import { dispatch } from '@/store/store';

interface HealthStatusChangeTableProps {
  data: any;
  setIsDirty: any;
  updateNotificationPreferences: any;
}

export const HealthStatusChangeTable = forwardRef<NotificationPreferencesTableRef, HealthStatusChangeTableProps>(
  ({ data, setIsDirty, updateNotificationPreferences }: HealthStatusChangeTableProps, ref) => {
    const { columns } = useHealthStatusChangeTable();
    const [currentData, setCurrentData] = useState<any>(data);
    const updateNestedParameter = useNestedParameterUpdate(setCurrentData);

    useEffect(() => {
      setCurrentData(data);
    }, [data]);

    useEffect(() => {
      setIsDirty(!isEqual(currentData, data));
    }, [currentData, data]);

    useImperativeHandle(ref, () => ({
      triggerSave: async (callback?: () => void): Promise<void> => {
        setIsDirty(false);

        // Manually update notification preferences rock size cache data
        dispatch(updateNotificationPreferencesCache('health_status_change', currentData));

        const payload = groupAssetsBySite(currentData);
        await updateNotificationPreferences(payload);

        callback?.();
      },
    }));

    if (!data?.length) {
      return <SectionEmptyState text="No assets were found based on the selected filters." />;
    }

    return (
      <BaseTable
        columns={columns}
        data={currentData}
        wrapHeaders={false}
        meta={{ updateData: updateNestedParameter }}
        classNames={NOTIFICATION_PREFERENCES_TABLE_CLASS_NAMES}
      />
    );
  }
);

HealthStatusChangeTable.displayName = 'HealthStatusChangeTable';
